import { lazy } from 'react';

/**
 * React.lazy that survives a deployment.
 *
 * Route chunks are content-hashed (PackageDetail-C5pR0Ebd.js). When a new build
 * is deployed, the old filenames disappear — but a browser that loaded the app
 * BEFORE the deploy still holds the old index.html and asks for the old name.
 * Nginx answers a missing /assets/* with the SPA fallback (index.html), the
 * browser refuses to execute HTML as a module, the dynamic import rejects, and
 * React renders nothing: a white screen.
 *
 * The only correct recovery is to fetch the new index.html, so we reload once.
 * A session-scoped timestamp guards against an infinite loop when the chunk is
 * genuinely broken rather than merely stale — in that case the error is allowed
 * to propagate to the error boundary.
 */
const RELOAD_KEY = 'sa_chunk_reload_at';
const RELOAD_COOLDOWN_MS = 15000;

export function lazyWithReload(factory) {
  return lazy(() =>
    factory().catch((error) => {
      let last = 0;
      try {
        last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
      } catch {
        /* private mode — treat as first failure */
      }

      if (Date.now() - last > RELOAD_COOLDOWN_MS) {
        try {
          sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        } catch {
          /* ignore */
        }
        // Bypass the HTTP cache so we're guaranteed the new asset map.
        window.location.reload(true);

        // Never resolves — the page is on its way out. Returning a pending
        // promise stops React from rendering an error flash mid-reload.
        return new Promise(() => {});
      }

      throw error;
    })
  );
}

/**
 * Vite fires this when a <link rel="modulepreload"> fails, which happens for
 * the same reason. Same recovery.
 */
export function installChunkReloadHandler() {
  window.addEventListener('vite:preloadError', (event) => {
    let last = 0;
    try {
      last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    } catch {
      /* ignore */
    }

    if (Date.now() - last > RELOAD_COOLDOWN_MS) {
      event.preventDefault();
      try {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
      } catch {
        /* ignore */
      }
      window.location.reload(true);
    }
  });
}

export default lazyWithReload;
